/**
 * useAISearchTelemetry — Iter 341 · Feb 12, 2026
 * ─────────────────────────────────────────────
 * Fires a lightweight beacon to /api/telemetry/pageview on every
 * React Router route change. The backend classifies the User-Agent
 * + Referer server-side and logs only AI-related visits (crawlers +
 * AI-referrer human clicks from ChatGPT, Perplexity, Gemini, Claude,
 * etc.) into db.ai_search_hits.
 *
 * Rationale: AI crawlers may hit HTML pages served by K8s ingress
 * without ever touching the backend — so a client-side beacon is the
 * only way to see human clicks originating from AI search products.
 * (Machine-only crawlers that skip JS still show up via the server-side
 * middleware for /api/* requests.)
 *
 * Mount ONCE at the app root (already done in App.js).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useAISearchTelemetry() {
  const location = useLocation();
  useEffect(() => {
    // Skip admin/staff routes and API endpoints — we only care about
    // AI-search traffic on the public marketing surface.
    const p = location.pathname || "/";
    if (p.startsWith("/admin") || p.startsWith("/staff") || p.startsWith("/api")) return;
    try {
      const body = JSON.stringify({
        path: p + (location.search || ""),
        referer: document.referrer || "",
      });
      // Prefer sendBeacon so navigation-away doesn't drop the request.
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(`${API}/telemetry/pageview`, blob);
      } else {
        // Fallback — best effort
        fetch(`${API}/telemetry/pageview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* noop — telemetry must never break rendering */ }
  }, [location.pathname, location.search]);
}
